import { notifications } from '@mantine/notifications';

interface ShowNotificationOptions {
    title: string;
    message: string;
    color?: string;
    loading?: boolean;
    autoClose?: number | boolean;
    withCloseButton?: boolean;
    id?: string;
}

/**
 * Show a notification with standardized settings to prevent UI freezes.
 * All notifications have a close button and will auto-close after some time.
 */
export const showSafeNotification = (options: ShowNotificationOptions) => {
    const {
        title,
        message,
        color = 'blue',
        loading = false,
        id,
    } = options;

    // Always show close button and auto-close even for loading notifications
    const autoClose = options.autoClose !== undefined ? options.autoClose : (loading ? 30000 : 5000);
    const withCloseButton = options.withCloseButton !== undefined ? options.withCloseButton : true;

    return notifications.show({
        id,
        title,
        message,
        color,
        loading,
        autoClose,
        withCloseButton,
    });
};

/**
 * Update a notification with standardized settings.
 * All notifications have a close button and will auto-close after some time.
 */
export const updateSafeNotification = (
    id: string,
    options: Omit<ShowNotificationOptions, 'id'>
) => {
    const {
        title,
        message,
        color = 'blue',
        loading = false,
    } = options;

    // Always show close button and auto-close even for loading notifications
    const autoClose = options.autoClose !== undefined ? options.autoClose : (loading ? 30000 : 5000);
    const withCloseButton = options.withCloseButton !== undefined ? options.withCloseButton : true;

    return notifications.update({
        id,
        title,
        message,
        color,
        loading,
        autoClose,
        withCloseButton,
    });
};

/**
 * Show a loading notification with standardized settings.
 * Includes timeout to prevent UI freezes.
 */
export const showLoadingNotification = (title: string, message: string) => {
    return showSafeNotification({
        title,
        message,
        loading: true,
        autoClose: 30000, // 30 seconds timeout
        withCloseButton: true,
    });
};

/**
 * Show an error notification with standardized settings.
 */
export const showErrorNotification = (error: unknown, fallbackMessage?: string) => {
    const errorMessage = error instanceof Error
        ? error.message
        : fallbackMessage || 'An unexpected error occurred';

    notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
    });
};

/**
 * Show a success notification with standardized settings.
 */
export const showSuccessNotification = (message: string, title?: string) => {
    notifications.show({
        title: title || 'Success',
        message,
        color: 'green',
    });
};

/**
 * Handle a Plaid webhook notification, updating the UI appropriately
 */
export const handlePlaidNotification = (
    notification: any,
    fetchAccounts: () => void,
    itemId?: string
) => {
    if (!notification || !notification.type) return;

    switch (notification.type) {
        case 'LOGIN_REPAIRED':
            // Process login repair notification
            showSuccessNotification(
                `Your ${notification.data?.institution_name || 'account'} has been reconnected`,
                notification.data?.message || 'Your account has been automatically reconnected'
            );
            // Refresh accounts to update UI
            fetchAccounts();
            break;

        case 'NEW_ACCOUNTS_AVAILABLE':
            // Process new accounts notification
            showSuccessNotification(
                `New accounts available at ${notification.data?.institution_name || 'your institution'}`,
                notification.data?.message || 'New accounts are available to add to your account'
            );
            // Refresh accounts to update UI
            fetchAccounts();
            break;

        case 'ITEM_UPDATE':
            // Only refresh if we don't have a specific itemId to filter by, 
            // or if the notification is for our specific item
            if (!itemId || notification.data?.item_id === itemId) {
                fetchAccounts();
            }
            break;

        default:
            // Unhandled notification type
            console.log('Unhandled notification type:', notification.type);
            break;
    }
}; 